import { find, isNumber } from "lodash";
import { useMemo } from "react";
import { amortizationMethodsListRealEstate, FormatNumber } from "utils";
import { useFormatCurrency } from "utils/customHooks/useFormatCurrency";

import type { Currency } from "@krea/shared/types/common";
import { formatLenderMonthlyInterestRate } from "@krea/shared/utils/formatNumber";

import { AmortizationMethodEnum } from "@/features/common/enums";
import { useGetCurrentLenderCompanyQuery } from "@/features/lender-companies/hooks/use-get-lender-company.query";
import type { IRealEstateBid } from "@/features/lender-responses/types/lender-response";

import type { IBidDetailsResponse } from "../../BidDetails";
import { BidDetailsListItem, EmptyField } from "../../bidHelpers";

interface IRealEstateBidProps {
  bid: IRealEstateBid;
  currency: Currency;
}

export const RealEstateBid = ({
  bid,
  currency,
}: IRealEstateBidProps): IBidDetailsResponse => {
  const [formattedCurrencyValue] = useFormatCurrency({ currency });

  const { data: lenderCompany } = useGetCurrentLenderCompanyQuery();

  const isInterestRatePerYearActivated =
    lenderCompany?.isFeatureInterestRatePerYearActivated;

  const averageMonthlyPayment = bid.averageMonthlyPayment;
  const comparableMonthlyInterestRate = bid.comparableMonthlyInterestRate;

  const amortizationMethodsText = find(
    amortizationMethodsListRealEstate,
    (method) => method.value === bid.amortizationMethod,
  );

  const paybackTime = bid.paybackTimeMonths;

  const details = useMemo(() => {
    return (
      <>
        <div>
          {!!amortizationMethodsText && !!amortizationMethodsText.label && (
            <BidDetailsListItem
              title="Payment plan"
              value={amortizationMethodsText.label}
            />
          )}

          {bid.amortizationMethod === AmortizationMethodEnum.LINEAR && (
            <>
              {isNumber(bid.amortizationDuringTerm) && (
                <BidDetailsListItem
                  title="Amortisation during loan term"
                  value={formattedCurrencyValue(bid.amortizationDuringTerm)}
                />
              )}
              {isNumber(bid.amortizationInPercent) && (
                <BidDetailsListItem
                  title="Amortisation in percent"
                  value={`${FormatNumber(bid.amortizationInPercent)}%`}
                />
              )}
            </>
          )}

          {bid.amortizationMethod !== AmortizationMethodEnum.FIXED &&
            isNumber(bid.lenderMonthlyInterest) && (
              <BidDetailsListItem
                title={`Nominal ${isInterestRatePerYearActivated ? "yearly" : "monthly"} interest rate (if specified in loan agreement)`}
                value={`${formatLenderMonthlyInterestRate(bid.lenderMonthlyInterest, "TO_YEARLY", isInterestRatePerYearActivated)}% / ${isInterestRatePerYearActivated ? "year" : "month"}`}
              />
            )}

          {bid.amortizationMethod === AmortizationMethodEnum.FIXED &&
            !!bid.lenderMonthlyAmount && (
              <BidDetailsListItem
                title="Fixed monthly fee (if specified in loan agreement)"
                value={`${formattedCurrencyValue(bid.lenderMonthlyAmount)} / month`}
              />
            )}

          {!!bid.setupFee && (
            <BidDetailsListItem
              title="Setup fee"
              value={formattedCurrencyValue(bid.setupFee)}
            />
          )}

          {(!!bid.amortizationDuringTerm ||
            !!bid.averageMonthlyCost ||
            !!bid.averageMonthlyAmortisation) && (
            <>
              {!!bid.amortizationDuringTerm &&
                bid.amortizationMethod !== AmortizationMethodEnum.LINEAR && (
                  <BidDetailsListItem
                    title="Amortisation during loan term"
                    value={formattedCurrencyValue(bid.amortizationDuringTerm)}
                  />
                )}
              {!!bid.averageMonthlyCost && (
                <BidDetailsListItem
                  title="Interest payment"
                  value={formattedCurrencyValue(bid.averageMonthlyCost)}
                />
              )}
              {!!bid.averageMonthlyAmortisation && (
                <BidDetailsListItem
                  title="Principal repayment"
                  value={formattedCurrencyValue(bid.averageMonthlyAmortisation)}
                />
              )}
            </>
          )}
          {!!bid.postalFee && (
            <BidDetailsListItem
              title="Postal fee (per month)"
              value={formattedCurrencyValue(bid.postalFee)}
            />
          )}

          {!!bid.directDebitFee && (
            <BidDetailsListItem
              title="Direct debit fee (per month)"
              value={formattedCurrencyValue(bid.directDebitFee)}
            />
          )}

          {!!bid.earlyRepaymentFee && (
            <BidDetailsListItem
              title="Cost for early repayment"
              value={
                /^\d+(\.\d+)?$/.test(bid.earlyRepaymentFee.trim())
                  ? formattedCurrencyValue(Number(bid.earlyRepaymentFee))
                  : bid.earlyRepaymentFee
              }
            />
          )}
        </div>
        <div className="mt-4">All fees are included in total loan cost</div>
      </>
    );
  }, [
    bid,
    formattedCurrencyValue,
    amortizationMethodsText,
    isInterestRatePerYearActivated,
  ]);

  const summary = useMemo(() => {
    return (
      <div className="flex w-full flex-col gap-2 xl:grid xl:grid-cols-4 xl:gap-4">
        <div className="flex flex-col gap-4">
          <BidDetailsListItem
            variant="summary"
            title="Amount"
            value={
              bid.amount ? formattedCurrencyValue(bid.amount) : <EmptyField />
            }
          />
        </div>
        <div className="flex flex-col gap-4">
          <BidDetailsListItem
            variant="summary"
            title="Loan term"
            value={
              paybackTime ? (
                `${paybackTime} ${paybackTime > 1 ? "months" : "month"}`
              ) : (
                <EmptyField />
              )
            }
          />
        </div>
        <div className="flex flex-col gap-4">
          <BidDetailsListItem
            variant="summary"
            title="Monthly payment"
            value={
              isNumber(averageMonthlyPayment) ? (
                formattedCurrencyValue(averageMonthlyPayment)
              ) : (
                <EmptyField />
              )
            }
          />
        </div>

        <div className="flex flex-col gap-4">
          <BidDetailsListItem
            variant="summary"
            title="Effective monthly interest"
            value={
              comparableMonthlyInterestRate ? (
                `${FormatNumber(comparableMonthlyInterestRate)}%`
              ) : (
                <EmptyField />
              )
            }
          />
        </div>
      </div>
    );
  }, [
    bid,
    formattedCurrencyValue,
    averageMonthlyPayment,
    comparableMonthlyInterestRate,
    paybackTime,
  ]);

  return {
    summary,
    details,
  };
};
